// Imports

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  collectPracticeOrdersAPI,
  fetchPratices,
  updatePracticeStatusAPI,
  fetchPracticeOrdersAPI,
  fetchOrdersHistoryAPI,
  fetchEnroutedPracticesAPI,
  fetchRoutePracticesAPI,
  validateQrAPI,
} from './api';
import { __filter__ } from '../../../utils/index';

// Creating Slice for practices Data
const practicesSlice = createSlice({
  name: 'practices',
  initialState: {
    totalPages: 0,
    loading: false,
    hasMore: false,
    practices: [],
    enroutedPractices: [],
    selectedOrders: [],
    practiceOrders: [],
    ordersHistory: [],
    error: '',
    orderCollectedTime: '',
    ordersCollected: false,
    checkInTime: '',
    checkInId: '',
  },

  reducers: {
    // This action will keep in sync error if any
    setError: (state, action) => {
      return { ...state, error: action.payload };
    },

    // This action will update EnRouted Order
    updateEnroutedPracticeOrders: (state, action) => {
      return {
        ...state,
        practiceOrders: action.payload,
      };
    },
    // This action will set practicesData
    setPractices: (state, action) => {
      return { ...state, practices: action.payload };
    },

    // This will setOrders History
    setOrdersHistory: (state, action) => {
      return { ...state, ordersHistory: action.payload };
    },

    // This will setEnroutedPractices
    setEnroutedPractices: (state, action) => {
      return { ...state, enroutedPractices: action.payload };
    },

    // This action will set CheckedOrders
    setSelectedOrders: (state, action) => {
      return { ...state, selectedOrders: action.payload };
    },
    // This action sets Order time when order has been collected
    setOrderCollectedTime: (state, action) => {
      return { ...state, orderCollectedTime: action.payload };
    },
    setCheckInId: (state, action) => {
      return { ...state, checkInId: action.payload };
    },

  },

  // adding cases for handling state life cycle of promise returned by thunk
  extraReducers: (builder) => {
    builder
      .addCase(fetchPracticesData.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchPracticesData.fulfilled, (state, action) => {
        return {
          ...state,
          practices: __filter__(
            [...state.practices, ...action.payload.data.practices],
            'name'
          ),
          totalPages: action.payload.data.total_pages,
          hasMore: action.payload.data.practices.length > 0,
          loading: false,
        };
      })
      .addCase(fetchPracticesData.rejected, (state, action) => {
        return { ...state, error: action.payload, loading: false };
      })
      .addCase(fetchEnroutedPractices.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchEnroutedPractices.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
          enroutedPractices: action.payload.data.practices,
        };
      })
      .addCase(fetchEnroutedPractices.rejected, (state, action) => {
        return { ...state, loading: false, error: action.payload };
      })
      .addCase(updatePracticeStatus.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(updatePracticeStatus.fulfilled, (state) => {
        return { ...state, loading: false };
      })
      .addCase(updatePracticeStatus.rejected, (state, action) => {
        return { ...state, error: action.payload, loading: false };
      })
      .addCase(fetchPracticeOrders.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchPracticeOrders.fulfilled, (state, action) => {
        return {
          ...state,
          practiceOrders: action.payload.data,
          loading: false,
        };
      })
      .addCase(fetchPracticeOrders.rejected, (state, action) => {
        return { ...state, error: action.payload, loading: false };
      })
      .addCase(fetchOrdersHistory.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchOrdersHistory.fulfilled, (state, action) => {
        return {
          ...state,
          ordersHistory: __filter__(
            [...state.ordersHistory, ...action.payload.data.practices],
            'name'
          ),
          totalPages: action.payload.data.total_pages,
          hasMore: action.payload.data.practices.length > 0,
          loading: false,
        };
      })
      .addCase(fetchOrdersHistory.rejected, (state, action) => {
        return { ...state, error: action.payload, loading: false };
      })
      .addCase(collectPracticeOrders.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(collectPracticeOrders.fulfilled, (state) => {
        return { ...state, loading: false, ordersCollected: true };
      })
      .addCase(collectPracticeOrders.rejected, (state, action) => {
        return { ...state, error: action.payload, loading: false };
      })
      .addCase(validateQr.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(validateQr.fulfilled, (state, action) => {
        return { ...state, loading: false, checkInId: action.payload.data.id };
      })
      .addCase(validateQr.rejected, (state, action) => {
        return { ...state, error: action.payload, loading: false };
      })
      .addCase(fetchRoutePractices.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchRoutePractices.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
          practices: action.payload.data.practices,
        };
      })
      .addCase(fetchRoutePractices.rejected, (state, action) => {
        return { ...state, error: action.payload, loading: false };
      });
  },
});

export const {
  setError,
  setPractices,
  setSelectedOrders,
  updateEnroutedPracticeOrders,
  setOrderCollectedTime,
  setEnroutedPractices,
  setOrdersHistory,
  setCheckInId,
} = practicesSlice.actions;
export default practicesSlice.reducer;

// Thunks Here
export const fetchPracticesData = createAsyncThunk(
  'user/practices',
  (data, thunkAPI) => {
    return fetchPratices(data, thunkAPI);
  }
);

export const fetchEnroutedPractices = createAsyncThunk(
  'user/EnroutedPractices',
  (data, { rejectWithValue, signal }) => {
    return fetchEnroutedPracticesAPI(data, rejectWithValue, signal);
  }
);

export const fetchPracticeOrders = createAsyncThunk(
  'user/practiceOrders',
  (data, { rejectWithValue }) => {
    return fetchPracticeOrdersAPI(data, rejectWithValue);
  }
);

export const updatePracticeStatus = createAsyncThunk(
  'user/practiceStatusUpdate',
  (data, { rejectWithValue }) => {
    return updatePracticeStatusAPI(data, rejectWithValue);
  }
);

export const collectPracticeOrders = createAsyncThunk(
  'practices/collectPracticeOrders',
  (data, { rejectWithValue }) => {
    return collectPracticeOrdersAPI(data, rejectWithValue);
  }
);

export const fetchOrdersHistory = createAsyncThunk(
  'practices/ordersHistory',
  (data, thunkAPI) => {
    return fetchOrdersHistoryAPI(data, thunkAPI);
  }
);

export const validateQr = createAsyncThunk(
  'practices/validateQr',
  (data, { rejectWithValue }) => {
    return validateQrAPI(data, rejectWithValue);
  }
);

export const fetchRoutePractices = createAsyncThunk(
  'practices/fetchRoutePractices',
  (data, { rejectWithValue }) => {
    return fetchRoutePracticesAPI(data, rejectWithValue);
  }
);
